/**
 * Wave 3 · 24 #8 — "Ready to bill" across the company: scheduled payment milestones (invoice rows still "Not
 * Started") whose work is finished on site, from GET /invoicing/ready-to-bill — the same rules as the project's own
 * Invoicing tab (Devices Installation done → Installation can be billed, …).
 *  - `ReadyToBillCallout`: the biggest one in a lime callout ("LIV Waterside — Devices Installation is 24/24 done,
 *    but Installation AED 193,782.82 has not been invoiced") with Raise invoice and View all (the register's
 *    "Ready to raise" view when the page passes `onViewAll`, else a list of every ready milestone with its evidence).
 *  - `RaiseInvoiceDialog`: invoice no. (required — the register and the payment reminder quote it), submitted on,
 *    optional due date → the row becomes Submitted (the shell's toast offers Undo — the change is recorded server-side).
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ReceiptText, Zap } from 'lucide-react';
import api from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { formatMoney, toDateInputValue } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { StatusPill } from '@/components/ui/status-pill';
import { notify } from '@/components/ui/notify';
import { SystemChip, plural } from './SfUi';

/** props: item ({ invoice_id, project_name, milestone, amount, evidence } or a register row), open, onOpenChange, onRaised. */
export function RaiseInvoiceDialog({ item, open, onOpenChange, onRaised }) {
  const today = toDateInputValue(new Date());
  const [form, setForm] = useState({ reference: '', submitted: today, due: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const refInput = useRef(null);
  useEffect(() => { if (open) { setForm({ reference: item?.reference || '', submitted: today, due: '' }); setError(''); setInvalid(false); setBusy(false); } }, [open, item, today]);
  if (!item) return null;
  const id = item.invoice_id || item.id;
  const submit = async (event) => {
    event.preventDefault();
    const reference = form.reference.trim();
    if (!reference) { setError('Enter the invoice number'); setInvalid(true); refInput.current?.focus(); return; }
    if (!form.submitted) { setError('Pick the day the invoice was sent'); return; }
    setBusy(true);
    setError('');
    try {
      const payload = { status: 'Submitted', submitted_at: form.submitted, reference };
      if (form.due) payload.due_date = form.due;
      const res = await api.put(`/invoices/${id}`, payload);
      onOpenChange(false);
      notify.success(`Invoice ${reference} raised`, { description: `${item.project_name || item.projectName} · ${item.milestone} · ${formatMoney(item.amount)}` });
      await onRaised?.(res.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not raise the invoice'));
      setBusy(false);
    }
  };
  const field = (key) => ({ value: form[key], onChange: (e) => setForm((prev) => ({ ...prev, [key]: e.target.value })) });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[440px] rounded-card border-line bg-surface text-ink" data-testid="raise-invoice-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="font-heading text-[17px] font-bold">Raise {item.milestone} invoice</DialogTitle>
            <DialogDescription className="text-[13px] text-ink-3">
              {item.project_name || item.projectName} · {formatMoney(item.amount)}{item.evidence?.length ? ` · ${item.evidence.join(' · ')}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1">
              <Label htmlFor="raise-ref" className="text-[12px] font-semibold text-ink-2">Invoice no. *</Label>
              <Input
                id="raise-ref"
                ref={refInput}
                className={cn('h-9 font-mono placeholder:font-sans placeholder:text-ink-4', invalid && 'border-[color:var(--status-red)]')}
                placeholder="e.g. 1150"
                aria-invalid={invalid || undefined}
                aria-describedby={invalid ? 'raise-ref-error' : undefined}
                value={form.reference}
                onChange={(e) => { const value = e.target.value; setForm((prev) => ({ ...prev, reference: value })); if (value.trim()) { setInvalid(false); setError(''); } }}
                data-testid="raise-invoice-ref"
              />
              {invalid ? <p id="raise-ref-error" className="text-[12px] font-semibold text-status-red-ink" data-testid="raise-invoice-ref-error">Enter the invoice number</p> : null}
            </div>
            <div className="space-y-1">
              <Label htmlFor="raise-on" className="text-[12px] font-semibold text-ink-2">Submitted on</Label>
              <Input id="raise-on" type="date" max={today} className="h-9" {...field('submitted')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="raise-due" className="text-[12px] font-semibold text-ink-2">Payment due <span className="font-normal text-ink-3">(optional)</span></Label>
              <Input id="raise-due" type="date" min={form.submitted || today} className="h-9" {...field('due')} />
            </div>
          </div>
          <p className="text-[12px] text-ink-3">Its status becomes Submitted and it moves to Awaiting payment.</p>
          {error && !invalid ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="raise-invoice-submit"><ReceiptText aria-hidden="true" />{busy ? 'Raising…' : 'Raise invoice'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReadyList({ items, canEdit, onRaise }) {
  return (
    <ul className="max-h-[360px] divide-y divide-line-2 overflow-y-auto" data-testid="ready-to-bill-list">
      {items.map((item) => (
        <li key={item.invoice_id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 px-3.5 py-2.5">
          <span className="min-w-0">
            <span className="flex min-w-0 items-center gap-1.5">
              <SystemChip system={item.system_type} className="h-[18px] min-w-[34px] text-[10px]" />
              <span className="truncate text-[13px] font-semibold text-ink">{item.project_name}</span>
              {item.project_status === 'On Hold' ? <StatusPill status="On Hold" domain="project" size="sm" className="shrink-0" data-testid={`ready-to-bill-on-hold-${item.invoice_id}`} /> : null}
            </span>
            <span className="block truncate text-[12px] text-ink-3">{item.milestone} · {item.evidence.join(' · ')}</span>
          </span>
          <span className="flex flex-col items-end gap-1">
            <span className="text-[13px] font-semibold tabular-nums text-ink">{formatMoney(item.amount)}</span>
            <span className="flex items-center gap-1">
              <Button variant="ghost" size="xs" asChild><Link to={`/projects/${item.project_id}?tab=invoicing`}>Open</Link></Button>
              {canEdit ? <Button variant="outline" size="xs" onClick={() => onRaise(item)} data-testid={`ready-to-bill-raise-${item.invoice_id}`}>Raise</Button> : null}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * props: data ({ items, total } — GET /invoicing/ready-to-bill, already narrowed to the page filters), canEdit,
 * onRaised (reload the page), onViewAll (open the register's "Ready to raise" view). Nothing when nothing is ready.
 */
export function ReadyToBillCallout({ data, canEdit, onRaised, onViewAll, className }) {
  const [raising, setRaising] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const items = Array.isArray(data?.items) ? data.items : [];
  if (!items.length) return null;
  const top = items[0];
  const more = items.length - 1;
  const raise = (item) => { setListOpen(false); setRaising(item); };
  return (
    <div
      className={cn('flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card border border-[color:var(--status-lime-bg)] bg-status-lime-bg px-4 py-3 text-[13.5px] text-ink', className)}
      role="note"
      data-testid="ready-to-bill"
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface text-status-lime-ink shadow-card" aria-hidden="true"><Zap className="h-4 w-4" /></span>
      <p className="min-w-0 flex-1 basis-[220px] leading-snug">
        <b className="font-bold">Ready to bill:</b> {top.project_name} — {top.evidence.join(', ')}, but {top.milestone} <b className="font-semibold tabular-nums">{formatMoney(top.amount)}</b> has not been invoiced yet.
        {more > 0 ? <span className="text-ink-3"> +{plural(more, 'more milestone')} · {formatMoney(data.total, { decimals: 0 })} in all</span> : null}
      </p>
      <span className="flex shrink-0 items-center gap-1.5">
        {canEdit ? (
          <Button variant="outline" size="sm" className="bg-surface" onClick={() => raise(top)} data-testid="ready-to-bill-raise">
            <ReceiptText aria-hidden="true" /> Raise invoice
          </Button>
        ) : null}
        {onViewAll ? (
          <Button variant="ghost" size="sm" onClick={onViewAll} data-testid="ready-to-bill-view-all">View all<ArrowRight aria-hidden="true" /></Button>
        ) : (
        <Popover open={listOpen} onOpenChange={setListOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" data-testid="ready-to-bill-view-all">View all<ArrowRight aria-hidden="true" /></Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[420px] max-w-[calc(100vw-24px)] rounded-xl border-line bg-surface p-0 text-ink shadow-overlay">
            <div className="border-b border-line-2 px-3.5 py-3">
              <p className="font-heading text-[13.5px] font-bold">Ready to bill · {plural(items.length, 'milestone')}</p>
              <p className="mt-0.5 text-[12px] text-ink-3">The work is done in Execution, the payment milestone is not invoiced yet · {formatMoney(data.total, { decimals: 0 })}</p>
            </div>
            <ReadyList items={items} canEdit={canEdit} onRaise={raise} />
          </PopoverContent>
        </Popover>
        )}
      </span>
      <RaiseInvoiceDialog item={raising} open={Boolean(raising)} onOpenChange={(open) => { if (!open) setRaising(null); }} onRaised={onRaised} />
    </div>
  );
}
